import { useState } from 'react';

import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { AutocompleteFilter, FilterOption } from '@/types/filters';

interface IProps {
  config: AutocompleteFilter;
  value: string[];
  onChange: (key: string, value: string[]) => void | boolean;
  displayDot?: boolean;
}

const TOGGLE_ALL_OPTION_VALUE = 'toggle-all';

export const FilterAutocomplete = ({ config, value, onChange }: IProps) => {
  const { t } = useTranslation();

  const selected = new Set(value);
  const [open, setOpen] = useState(false);

  const allValues = config.options.map(option => option.value);
  const selectedLabels = value.map(v => config.options.find(o => o.value === v)?.label ?? v);

  const isAllValuesSelected = selected.size === allValues.length;

  const label = `: ${isAllValuesSelected ? t('common:filters.selectAllOption') : selectedLabels.join(', ')}`;

  const toggle = (value: string) => {
    let result: void | boolean;

    if (value === TOGGLE_ALL_OPTION_VALUE && isAllValuesSelected) {
      result = onChange(config.name, []);
    } else if (value === TOGGLE_ALL_OPTION_VALUE && !isAllValuesSelected) {
      result = onChange(config.name, allValues);
    } else {
      const newSelected = new Set(selected);
      if (selected.has(value)) {
        newSelected.delete(value);
      } else {
        newSelected.add(value);
      }

      result = onChange(config.name, Array.from(newSelected));
    }

    // Close popover if change was prevented (returned false)
    if (result === false) {
      setOpen(false);
    }
  };

  const onClear = () => {
    onChange(config.name, []);
  };

  const renderSelectAllLabel = () => (
    <div className={cn('flex items-center justify-start gap-2')}>
      <span className={cn('text-sm')}>{t('common:filters.selectAllOption')}</span>
    </div>
  );

  const renderLabel = (opt: FilterOption) => {
    return config.CustomLabelComponent ? (
      config.CustomLabelComponent(opt.label, opt)
    ) : (
      <div
        className={cn('flex items-center justify-start gap-2', opt.color && `text-${opt.color}`)}
      >
        {config.displayDot && (
          <div className="flex h-[16px] w-[16px] min-w-[16px] items-center justify-center">
            <span
              className="block h-[6.6px] min-h-[6.6px] w-[6.6px] min-w-[6.6px] rounded-full"
              style={{ backgroundColor: `currentColor` }}
            />
          </div>
        )}
        <span className={cn('text-sm')}>{opt.label}</span>
      </div>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn('h-9 px-2 py-1 border rounded text-sm text-left', open && 'bg-muted')}
      >
        <div className="flex items-center justify-between gap-2">
          <span>
            {config.label}
            <span className="font-medium">{value.length > 0 ? label : ''}</span>
          </span>

          {<ChevronDown className={cn('transition', open && 'rotate-180')} size={16} />}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" side="bottom" align="start">
        <Command>
          {config.withSearch && (
            <CommandInput
              placeholder={t('common:filters.search', { value: config.label })}
              className="h-9"
            />
          )}
          <CommandList className="p-2">
            {config.emptyMessage && <CommandEmpty>{config.emptyMessage}</CommandEmpty>}
            {config.selectAllOption && (
              <CommandItem
                onSelect={() => toggle(TOGGLE_ALL_OPTION_VALUE)}
                className={cn(
                  'flex items-center cursor-pointer',
                  isAllValuesSelected && 'bg-muted'
                )}
              >
                <Checkbox checked={isAllValuesSelected} />
                <Label> {renderSelectAllLabel()}</Label>
              </CommandItem>
            )}
            {config.options.map(opt => {
              const isSelected = selected.has(opt.value);
              return (
                <CommandItem
                  key={opt.value}
                  onSelect={() => toggle(opt.value)}
                  className={cn('flex items-center cursor-pointer', isSelected && 'bg-muted')}
                >
                  <Checkbox checked={isSelected} id={opt.value} />
                  <Label> {renderLabel(opt)}</Label>
                </CommandItem>
              );
            })}
          </CommandList>
          <CommandSeparator />
          <CommandGroup className="p-2">
            <div className="flex items-center justify-between">
              <Button size="sm" className="h-8" onClick={onClear} disabled={selected.size === 0}>
                {t('common:filters.clearAll')}
              </Button>
              <p className="text-sm text-muted-foreground">
                {t('common:filters.selected', { count: selected.size })}
              </p>
            </div>
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
